// useProximityAlerts — vigila la geolocalización y dispara una alerta
// cuando el usuario entra en el radio de un "venue" asociado a un sabor.
//
// Características:
//   • Radio configurable (default 200m)
//   • Cooldown por venue/sabor (default 1h) persistido en localStorage
//   • Opción de descartar por el resto del día (botón "no mostrar más hoy")
//   • Sólo dispara mientras la app está abierta (limitación realista de iOS PWA)

import { useEffect, useRef, useState } from "react";
import { distanceMeters } from "./useGeolocation";

const STORAGE_KEY = "proximity-alerts-v1";
const DEFAULT_RADIUS = 200; // metros
const DEFAULT_COOLDOWN_MS = 60 * 60 * 1000; // 1 hora

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* noop */
  }
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

// items: array de { tastingId, tastingName, tastingEmoji, city, venue: { name, lat, lng }, what, tip }
export function useProximityAlerts({
  position,
  items,
  radius = DEFAULT_RADIUS,
  cooldownMs = DEFAULT_COOLDOWN_MS,
  enabled = true,
}) {
  const [activeAlert, setActiveAlert] = useState(null);
  const lastFiredRef = useRef(loadState());

  useEffect(() => {
    if (!enabled || !position || !items || items.length === 0) return;

    const state = lastFiredRef.current;
    const now = Date.now();
    const today = todayKey();

    // Encontrar el primer venue dentro del radio cuyo cooldown haya pasado
    for (const item of items) {
      const v = item.venue;
      if (!v || typeof v.lat !== "number" || typeof v.lng !== "number") continue;

      const d = distanceMeters(position, { lat: v.lat, lng: v.lng });
      if (d == null || d > radius) continue;

      const key = `${item.tastingId}__${v.name}`;
      const entry = state[key] || {};
      // Silenciado por el resto del día
      if (entry.snoozeDay === today) continue;
      // Cooldown
      if (entry.lastFired && now - entry.lastFired < cooldownMs) continue;

      // Disparar
      state[key] = { ...entry, lastFired: now };
      saveState(state);
      setActiveAlert({ ...item, distance: d, key });
      break; // sólo una alerta a la vez
    }
  }, [position, items, radius, cooldownMs, enabled]);

  const dismiss = () => setActiveAlert(null);

  const snoozeToday = () => {
    if (!activeAlert) return;
    const state = lastFiredRef.current;
    const today = todayKey();
    state[activeAlert.key] = { ...(state[activeAlert.key] || {}), snoozeDay: today };
    saveState(state);
    setActiveAlert(null);
  };

  return { activeAlert, dismiss, snoozeToday };
}

// Devuelve el venue más cercano a "from" entre los venues de un tasting.
// tasting: { venues: [{name, lat, lng}, ...] }
export function nearestVenue(from, tasting) {
  if (!from || !tasting?.venues?.length) return null;
  let best = null;
  let bestDist = Infinity;
  for (const v of tasting.venues) {
    if (typeof v.lat !== "number" || typeof v.lng !== "number") continue;
    const d = distanceMeters(from, { lat: v.lat, lng: v.lng });
    if (d != null && d < bestDist) {
      bestDist = d;
      best = v;
    }
  }
  return best ? { ...best, distance: bestDist } : null;
}
